import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Pause, Play, Volume2, VolumeX, Waves, Sparkles } from "lucide-react";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";
import { getVoicePref } from "@/lib/voicePref";
import { isSoundEnabled } from "@/lib/chime";
import {
  startBreathAmbience, stopBreathAmbience, duckBreathAmbience,
} from "@/lib/breathAmbience";
import { scaleToTarget, totalMs, speakMs } from "@/lib/meditationVariants";

/**
 * InnateAbilitiesMeditation — "Remembering Your Innate Abilities"
 *
 * A gentle five-section practice that walks the member through five
 * innate capacities they were born with — regulation, intuition,
 * emotional intelligence, healing, and expansion — with a soft opening,
 * a bridging "returning to what's already yours" beat, and a closing
 * that carries the remembering back into the day.
 *
 * Delivered as a beat sequence — speech beats are read by ElevenLabs
 * (context=meditation → Kristen/Locke), phase beats render the section
 * label softly above the stage, and pause beats hold real silence. All
 * three variants (5m Beginner / 10m Intermediate / 20m Advanced) land
 * on their exact wall-clock target via `scaleToTarget` in
 * `lib/meditationVariants.js`.
 */

const BEATS = [
  // ------- Opening / settling -------
  { type: "speak", text: "Take a moment to settle into a comfortable position. Let your body relax. Let your breath soften. Let yourself arrive fully into this moment." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Gently close your eyes. Feel the weight of your body. Feel the support beneath you. Feel your breath moving in and out, naturally." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "There is nothing you need to change. Nothing you need to fix. Just allow yourself to be here." },

  // ------- Returning to what is already yours -------
  { type: "phase", label: "Returning to what is already yours" },
  { type: "speak", text: "Bring your awareness to the center of your chest — the quiet space behind your breath." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "This meditation is not about becoming someone new. It is about remembering what has always been yours." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Take a slow inhale… and a slow exhale." },
  { type: "pause", ms: 10000 },

  // ------- Regulation -------
  { type: "phase", label: "Innate ability · Regulation" },
  { type: "speak", text: "Feel your breath moving through your body. Notice how your system responds — softening, settling, recalibrating." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "You were born with the ability to regulate your own state. Your body knows how to find balance. Your breath knows how to guide you home." },
  { type: "pause", ms: 15000 },

  // ------- Intuition -------
  { type: "phase", label: "Innate ability · Intuition" },
  { type: "speak", text: "Now bring your awareness to the subtle sensations in your chest and belly. The quiet signals. The gentle knowing." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Your intuition is not something you learn. It is something you remember. It has always been here." },
  { type: "pause", ms: 15000 },

  // ------- Emotional Intelligence -------
  { type: "phase", label: "Innate ability · Emotional intelligence" },
  { type: "speak", text: "Notice any emotion present — calm, heaviness, openness, neutrality. Whatever is here is welcome." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Your emotional system is not a flaw. It is a guidance system. You were born with the ability to feel, understand, and respond." },
  { type: "pause", ms: 15000 },

  // ------- Healing -------
  { type: "phase", label: "Innate ability · Healing" },
  { type: "speak", text: "Bring your awareness to your whole body. The breath. The warmth. The subtle movement of life within you." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Your body repairs. Your mind reorganizes. Your heart recalibrates. Healing is not something you chase — it is something you allow." },
  { type: "pause", ms: 15000 },

  // ------- Expansion -------
  { type: "phase", label: "Innate ability · Expansion" },
  { type: "speak", text: "Feel the space around you. Feel the space within you. Feel how naturally you expand when you stop resisting yourself." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "Growth is not a skill. It is a biological truth. You were born with the ability to evolve." },
  { type: "pause", ms: 15000 },

  // ------- Closing -------
  { type: "phase", label: "Closing" },
  { type: "speak", text: "Take one slow inhale… and one full exhale." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "Feel your innate abilities alive within you — regulation, intuition, emotional intelligence, healing, expansion." },
  { type: "pause", ms: 15000 },
  { type: "speak", text: "They are not outside you. They are not distant. They are not conditional. They are part of your design." },
  { type: "pause", ms: 10000 },
  { type: "speak", text: "When you are ready… gently open your eyes. Carry this remembering with you." },
];

function beatDurationMs(beat) {
  if (beat.type === "pause") return beat.ms || 0;
  if (beat.type === "phase") return 800;
  if (beat.type === "speak") return speakMs(beat.text);
  return 0;
}

const TOTAL_MS = BEATS.reduce((acc, b) => acc + beatDurationMs(b), 0);

// Wall-clock targets by variant. `scaleToTarget` picks the exact
// pause + breath multiplier that lands on `targetMs` given the fixed
// speech cost, then compensates any rounding drift on pauses.
const TARGETS = {
  beginner:      5 * 60 * 1000,
  intermediate: 10 * 60 * 1000,
  advanced:     20 * 60 * 1000,
};

export default function InnateAbilitiesMeditation({ open, onClose, variant = "beginner" }) {
  const target = TARGETS[variant] ?? TARGETS.beginner;
  const beats = React.useMemo(() => scaleToTarget(BEATS, target, variant), [target, variant]);
  const TOTAL_MS_LOCAL = React.useMemo(() => totalMs(beats), [beats]);
  const [beatIdx, setBeatIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [narrating, setNarrating] = useState(() => canSpeak() && getVoicePref().enabled);
  const [ambience, setAmbience] = useState(() => isSoundEnabled());
  const [elapsedMs, setElapsedMs] = useState(0);
  const [currentPhase, setCurrentPhase] = useState("");
  const cancelledRef = useRef(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    setBeatIdx(0);
    setPaused(false);
    setElapsedMs(0);
    setCurrentPhase("");
  }, [open]);

  useEffect(() => () => {
    cancelledRef.current = true;
    stopSpeaking();
    stopBreathAmbience();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (!open) { stopBreathAmbience(); return undefined; }
    if (!ambience) { stopBreathAmbience(); return undefined; }
    startBreathAmbience();
    return () => { /* handled by open==false branch */ };
  }, [open, ambience]);

  useEffect(() => {
    if (!ambience || !open) return;
    duckBreathAmbience(paused ? 0.35 : 1.0, 0.6);
  }, [paused, ambience, open]);

  useEffect(() => {
    if (!open || paused || cancelledRef.current) return undefined;
    const beat = beats[beatIdx];
    if (!beat) return undefined;
    let cleanup = () => {};

    const advance = () => {
      if (cancelledRef.current) return;
      setElapsedMs((ms) => ms + beatDurationMs(beat));
      if (beatIdx + 1 >= beats.length) return;
      setBeatIdx(beatIdx + 1);
    };

    if (beat.type === "speak") {
      if (narrating && canSpeak()) {
        speak(beat.text, { context: "meditation", onEnd: advance });
        cleanup = () => stopSpeaking();
      } else {
        timeoutRef.current = setTimeout(advance, beatDurationMs(beat));
        cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
      }
    } else if (beat.type === "pause") {
      timeoutRef.current = setTimeout(advance, beat.ms || 0);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    } else if (beat.type === "phase") {
      setCurrentPhase(beat.label);
      timeoutRef.current = setTimeout(advance, 800);
      cleanup = () => timeoutRef.current && clearTimeout(timeoutRef.current);
    }

    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beatIdx, open, paused, narrating]);

  useEffect(() => {
    if (paused) {
      stopSpeaking();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [paused]);

  useEffect(() => {
    const onPref = () => {
      const enabled = getVoicePref().enabled;
      if (!enabled) { setNarrating(false); stopSpeaking(); }
    };
    window.addEventListener("n8-voice-pref-changed", onPref);
    return () => window.removeEventListener("n8-voice-pref-changed", onPref);
  }, []);

  if (!open) return null;

  const beat = beats[beatIdx];
  const finished = beatIdx >= beats.length - 1 && !paused;
  const progress = Math.min(100, Math.round((elapsedMs / TOTAL_MS_LOCAL) * 100));
  const displayText = beat?.type === "speak" ? beat.text : "";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Remembering your innate abilities meditation"
      data-testid="innate-abilities-overlay"
    >
      {/* Warm dawn backdrop — the theme is remembering something ancient */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(18, 14, 12, 0.985)" }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 42%, rgba(220,180,140,0.16) 0%, rgba(90,60,45,0.05) 45%, transparent 78%)",
        }}
      />

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5" data-testid="innate-abilities-progress-track">
        <div
          className="h-full bg-[hsl(var(--terracotta))]/70 transition-[width] duration-700"
          style={{ width: `${progress}%` }}
          data-testid="innate-abilities-progress-bar"
        />
      </div>

      {currentPhase && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-white/60"
          data-testid="innate-abilities-phase"
        >
          {currentPhase}
        </div>
      )}

      <button
        type="button"
        onClick={onClose}
        aria-label="Close meditation"
        data-testid="innate-abilities-close"
        className="absolute top-4 right-4 z-30 rounded-full p-2.5 text-white/80 hover:text-white bg-white/5 hover:bg-white/15 tab-link"
      >
        <X size={18} />
      </button>

      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center px-8 text-center gap-10">
        <InnateGlow />

        <div className="max-w-2xl min-h-[3.5em]">
          <p
            key={beatIdx}
            className="font-display italic text-white/90 text-2xl md:text-3xl leading-relaxed animate-[fade-up_800ms_ease-out]"
            data-testid="innate-abilities-text"
          >
            {displayText}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? "Resume meditation" : "Pause meditation"}
            data-testid="innate-abilities-toggle"
            className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/10 tab-link"
            disabled={finished}
          >
            {paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          {canSpeak() && (
            <button
              type="button"
              onClick={() => setNarrating((n) => !n)}
              aria-label={narrating ? "Mute N8's voice" : "Unmute N8's voice"}
              aria-pressed={narrating}
              data-testid="innate-abilities-narration-toggle"
              className={`rounded-full p-2 tab-link ${
                narrating
                  ? "text-white bg-white/15 hover:bg-white/25"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
              disabled={finished}
            >
              {narrating ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setAmbience((a) => !a)}
            aria-label={ambience ? "Mute breathing ambience" : "Play breathing ambience"}
            aria-pressed={ambience}
            data-testid="innate-abilities-ambience-toggle"
            title={ambience ? "Ambience on" : "Ambience off"}
            className={`rounded-full p-2 tab-link ${
              ambience
                ? "text-white bg-white/15 hover:bg-white/25"
                : "text-white/60 hover:text-white hover:bg-white/10"
            }`}
            disabled={finished}
          >
            <Waves size={15} />
          </button>
        </div>

        {finished && (
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60" data-testid="innate-abilities-complete">
            Meditation complete
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * InnateGlow — a soft warm-amber sparkle that gently pulses. The theme
 * is inner light being remembered, so the visual leans dawn rather
 * than night. Same footprint as the other meditation ring visuals so
 * the tab feels cohesive.
 */
function InnateGlow() {
  return (
    <div className="relative grid place-items-center h-[300px] w-[300px]" data-testid="innate-abilities-glow">
      <div
        aria-hidden
        className="absolute rounded-full"
        style={{
          width: 260,
          height: 260,
          animation: "innate-aura-pulse 10s ease-in-out infinite",
          background: "radial-gradient(circle, rgba(220,180,140,0.28) 0%, rgba(220,180,140,0) 68%)",
        }}
      />
      <div
        aria-hidden
        className="absolute rounded-full border border-white/30"
        style={{
          width: 200,
          height: 200,
          animation: "innate-ring-pulse 10s ease-in-out infinite",
        }}
      />
      <Sparkles
        aria-hidden
        size={40}
        strokeWidth={1.4}
        className="text-[hsl(var(--terracotta))] relative z-10"
        style={{ animation: "innate-glyph-pulse 10s ease-in-out infinite" }}
      />
      <style>{`
        @keyframes innate-ring-pulse {
          0%, 100% { transform: scale(0.95); opacity: 0.55; }
          50%      { transform: scale(1.06); opacity: 1;    }
        }
        @keyframes innate-aura-pulse {
          0%, 100% { opacity: 0.55; transform: scale(0.96); }
          50%      { opacity: 1;    transform: scale(1.08); }
        }
        @keyframes innate-glyph-pulse {
          0%, 100% { transform: scale(0.96); opacity: 0.85; }
          50%      { transform: scale(1.04); opacity: 1;    }
        }
      `}</style>
    </div>
  );
}
